import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { trackMessengerClick, trackGA4 } from '../utils/analytics';
import { priceFor, messengerFor, offerText } from '../utils/batteryModels';

// Újrahasznosítható, kereshető modell-lista a szerviz-landingekhez.
// Kereső (azonnali szűrés, nyíl/Enter navigáció), 20 modell + „Továbbiak”,
// dinamikus találatszám, empty state, teljes sor kattintható (Messenger).
// Konfigurálható eseménynévvel és szolgáltatás-címkével.

const INITIAL = 20;

export default function ModelSearchList({
  models = [],
  messengerKind = 'akku',
  serviceLabel = 'akkumulátorcserére',
  gaPrefix = 'battery',
  autoFocusDesktop = true,
  // „…-tól” címke azokra a modellekre, amikre nincs tételes ár az árlistában.
  // Oldalanként más: iPhone-nál az árlistából számolt minimum, Samsung/Xiaominál
  // a landing saját ígérete. Ide SOHA ne kerüljön kitalált szám.
  priceFallback,
}) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [hi, setHi] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!autoFocusDesktop) return;
    try {
      if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
        inputRef.current?.focus({ preventScroll: true });
      }
    } catch (e) { /* no-op */ }
  }, [autoFocusDesktop]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => m.toLowerCase().includes(q));
  }, [query, models]);

  const isSearching = query.trim().length > 0;
  const shown = useMemo(
    () => (isSearching ? filtered : (showAll ? models : models.slice(0, INITIAL))),
    [isSearching, filtered, showAll, models],
  );

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (hi >= 0 && shown[hi]) {
        trackMessengerClick(`lp_${gaPrefix}_row_kbd`);
        trackGA4(`${gaPrefix}_messenger_click`, { model: shown[hi] });
        window.open(messengerFor(shown[hi], messengerKind), '_blank', 'noopener');
      } else if (shown.length > 0) {
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (e.key === 'Escape') { setQuery(''); setHi(-1); }
  };

  return (
    <>
      <p className="text-sm text-gray-500 mb-4 text-center">
        Jelenleg <span className="font-semibold text-brand-hover">{models.length}</span> támogatott modell — a lista automatikusan frissül a készülékadatbázisból.
      </p>

      <div className="relative mb-2">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            const v = e.target.value;
            if (!query.trim() && v.trim()) trackGA4(`${gaPrefix}_models_search`);
            setQuery(v); setShowAll(false); setHi(-1);
          }}
          onKeyDown={onSearchKey}
          placeholder="Keresd a telefonodat…"
          aria-label="Modell keresése"
          autoComplete="off"
          className="w-full pl-11 pr-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-brand outline-none transition-colors"
          data-testid={`${gaPrefix}-model-search`}
        />
      </div>

      <p className="text-xs font-semibold text-gray-500 mb-4 px-1" aria-live="polite" data-testid={`${gaPrefix}-count`}>
        {isSearching ? `${filtered.length} találat` : `${models.length} modell`}
      </p>

      {isSearching && filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white px-6 py-12 text-center" data-testid={`${gaPrefix}-empty`}>
          <div className="text-4xl mb-3" aria-hidden="true">📱</div>
          <p className="font-bold text-ink mb-1">Nem található ilyen modell.</p>
          <p className="text-gray-500 text-sm mb-5">Ettől még lehet, hogy tudunk segíteni – kérj egyedi ajánlatot.</p>
          <a
            href={messengerFor(query.trim(), messengerKind)}
            onClick={() => { trackMessengerClick(`lp_${gaPrefix}_empty`); trackGA4(`${gaPrefix}_quote_click`, { source: 'empty_state' }); }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white font-bold rounded-full hover:bg-brand-hover transition-colors"
          >
            <MessageCircle size={18} /> Kérj egyedi ajánlatot
          </a>
        </div>
      ) : (
        <>
          {/* Egyszerű kereshető LINK-lista, nem listbox: minden sor valódi
              <a> a Messengerbe – a korábbi role="option" elnyomta a
              link-szerepet (nem hangzott el, hogy új lapon nyíló hivatkozás).
              A nyílbillentyűs kiemelés kényelmi extra, a linkek Tab-bal
              natívan bejárhatók. */}
          <div ref={listRef} className="scroll-mt-24 rounded-2xl border border-gray-200 overflow-hidden shadow-sm" aria-label="Támogatott modellek">
            {shown.map((model, i) => (
              <a
                key={model}
                href={messengerFor(model, messengerKind)}
                onClick={() => { trackMessengerClick(`lp_${gaPrefix}_row`); trackGA4(`${gaPrefix}_messenger_click`, { model }); }}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setHi(i)}
                title={offerText(model, serviceLabel)}
                className={`group flex items-center justify-between gap-3 px-4 sm:px-5 py-3 cursor-pointer transition-colors hover:bg-brand/10 ${hi === i ? 'bg-brand/10' : (i % 2 ? 'bg-gray-50' : 'bg-white')}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-ink text-sm sm:text-base truncate">{model}</p>
                  <p className="text-xs text-brand-hover font-semibold">{priceFor(model, priceFallback)}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand/10 text-brand-hover text-sm font-semibold rounded-full whitespace-nowrap flex-shrink-0 transition-all group-hover:bg-brand group-hover:text-white group-hover:gap-2">
                  <MessageCircle size={14} /> Ajánlatot kérek
                </span>
              </a>
            ))}
          </div>

          {!isSearching && models.length > INITIAL && (
            <div className="text-center mt-4">
              {!showAll ? (
                <button
                  type="button"
                  onClick={() => { setShowAll(true); trackGA4(`${gaPrefix}_show_more`); }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-brand text-brand-hover font-semibold rounded-full hover:bg-brand/5 transition-colors"
                  data-testid={`${gaPrefix}-show-all`}
                >
                  <ChevronDown size={18} /> További modellek megjelenítése ({models.length - INITIAL})
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAll(false)}
                  className="inline-flex items-center gap-2 px-6 py-3 text-gray-500 font-semibold hover:text-brand transition-colors"
                >
                  <ChevronUp size={18} /> Kevesebb
                </button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
